import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteLayout from "@/components/feature/SiteLayout";
import { useSiteInfo } from "@/hooks/useSiteInfo";
import type { ProductItem } from "@/lib/types";

export default function ProductDetailPage() {
  const { t } = useTranslation();
  const site = useSiteInfo();
  const { id } = useParams<{ id: string }>();
  const allProducts = t("products.items", { returnObjects: true }) as unknown as ProductItem[];
  const product = allProducts.find((p) => p.id === id);

  if (!product) {
    return (
      <SiteLayout navVariant="solid">
        <section className="pt-[120px] pb-20 bg-background-50">
          <div className="max-w-[640px] mx-auto px-6 text-center">
            <span className="w-20 h-20 mx-auto rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-inbox-line text-foreground-400 text-3xl" />
            </span>
            <h2 className="mt-6 text-2xl font-heading font-extrabold text-foreground-950">
              {t("products.detail.notFoundTitle")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600">
              {t("products.detail.notFoundDesc")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                {t("products.detail.backToList")}
              </Link>
              <Link
                to="/quotation"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line" />
                {t("products.detail.quoteInquiry")}
              </Link>
            </div>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout navVariant="solid">
      {/* Breadcrumb */}
      <section className="pt-[72px] bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="breadcrumb">
            <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">
              HOME
            </Link>
            <i className="ri-arrow-right-s-line text-[10px]" />
            <Link to="/products" className="hover:text-foreground-900 transition-colors cursor-pointer">
              {t("products.detail.breadcrumbProducts")}
            </Link>
            <i className="ri-arrow-right-s-line text-[10px]" />
            <span className="text-foreground-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </section>

      {/* Product Detail */}
      <section className="pb-16 md:pb-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Left: Image */}
            <div className="lg:w-1/2">
              <div className="aspect-square rounded-2xl overflow-hidden bg-background-100 border border-background-200/70">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right: Info */}
            <div className="lg:w-1/2 flex flex-col">
              <div>
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                    {product.category}
                  </span>
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-bold">
                    {product.subcategory}
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950">
                  {product.name}
                </h1>
                <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
                  {product.description}
                </p>
              </div>

              {/* Technical Specs */}
              <div className="mt-8 p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="font-heading font-bold text-sm text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-settings-3-line text-primary-500" />
                  {t("products.detail.specsTitle")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <SpecRow label={t("products.detail.specApplication")} value={product.application} />
                  <SpecRow label={t("products.detail.specMaterial")} value={product.material} />
                  <SpecRow label={t("products.detail.specHardness")} value={product.hardness} />
                  <SpecRow label={t("products.detail.specTempRange")} value={product.tempRange} />
                  <SpecRow label={t("products.detail.specProduction")} value={t("products.detail.specProductionValue")} />
                  <SpecRow label={t("products.detail.specMOQ")} value={t("products.detail.specMOQValue")} />
                </div>
              </div>

              {/* Features */}
              <div className="mt-6">
                <h3 className="font-heading font-bold text-sm text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-star-line text-primary-500" />
                  {t("products.detail.featuresTitle")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {product.features.map((feat, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-background-100 border border-background-200/70">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-primary-500 text-sm" />
                      </span>
                      <span className="text-xs text-foreground-700 leading-relaxed">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Drawing Available Notice */}
              {product.drawingAvailable && (
                <div className="mt-5 p-4 rounded-xl bg-accent-50 border border-accent-200/60">
                  <p className="text-sm text-foreground-700 flex items-start gap-2">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-file-copy-2-line text-accent-500" />
                    </span>
                    {t("products.detail.drawingNotice")}
                  </p>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/quotation"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line" />
                  {t("products.detail.quoteRequest")}
                </Link>
                <a
                  href={site.telLink}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-background-300 text-foreground-800 hover:bg-background-100 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line" />
                  {t("products.detail.phoneConsult")}
                </a>
              </div>

              {/* Bottom Nav */}
              <div className="mt-6 flex flex-wrap gap-4">
                <Link
                  to="/products"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  {t("products.detail.productList")}
                </Link>
                <Link
                  to="/technical"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-file-list-3-line" />
                  {t("products.detail.techData")}
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-customer-service-2-line" />
                  {t("products.detail.inquiry")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-foreground-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-foreground-900">{value}</p>
    </div>
  );
}